"use client"

import { Loader2 } from "lucide-react"
import { Link } from "next-view-transitions"
import Image from "next/image"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Suspense, useCallback, useEffect, useState } from "react"
import { FaApple } from "react-icons/fa"
import { FcGoogle } from "react-icons/fc"
import { toast } from "sonner"

import { clearAuthCookies } from "@/lib/api/auth-session"
import { newFrontendURL } from "@/lib/new-url"
import { cn, t } from "@/lib/utils"

import HeadingText from "@/components/heading-text"
import SignInForm from "@/components/nango/common/components/forms/sign-in-form"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"

import {
  AuthIntent,
  confirmLoginCode,
  redirectIfCookies,
  requestLoginCode,
  signInWithProvider,
} from "@/actions/signIn"
import { useBotTrap } from "@/hooks/use-bot-trap"
import { Config, Translation } from "@/types/nango"

const LAST_AUTH_METHOD_KEY = "last-auth-method"
const LAST_AUTH_EMAIL_KEY = "last-auth-email"

type AuthMethod = "email" | "google" | "apple"

const translation: Translation = {
  login: { EN: "Welcome", FR: "Bienvenue" },
  "last-used": { EN: "Recent", FR: "Récent" },
  welcome: {
    EN: "Sign in or create your account",
    FR: "Connectez-vous ou créez votre compte",
  },
  "no-account": {
    EN: "No account? No problem, we'll create one for you automatically.",
    FR: "Pas de compte ? Aucun problème, nous en créerons un pour vous automatiquement.",
  },
  or: { EN: "or", FR: "ou" },
  and: { EN: "and", FR: "et" },
  "legal-1": {
    EN: "By using this application, you agree to your ",
    FR: "En utilisant cette application, vous acceptez nos ",
  },
  "legal-2": { EN: "terms of service", FR: "conditions de service" },
  "legal-3": { EN: "privacy policy", FR: "politique de confidentialité" },
  "legal-4": { EN: "legal notice", FR: "mentions légales" },
  soon: { EN: "Coming soon", FR: "Arrive prochainement" },
  title: { EN: "One last step", FR: "Renseignez le code" },
  subtitle: { EN: "We have sent a code to", FR: "Nous avons envoyé un code à" },
  resend: { EN: "Resend code", FR: "Renvoyer le code" },
  "connect-success": {
    EN: "Connected successfully",
    FR: "Connecté avec succès",
  },
  "resend-toast": {
    EN: "A new email has been sent. Did the previous email land in your spam folder?",
    FR: "Un nouveau mail a été envoyé. Veuillez verifier vos spams.",
  },
  "otp-too-many-attempts": {
    EN: "Too many failed attempts. Account blocked for 5 minutes.",
    FR: "Trop d'essais infructueux. Compte bloqué pendant 5 minutes.",
  },
  "otp-invalid-code": {
    EN: "Invalid code. Please try again.",
    FR: "Code incorrect. Merci de réessayer.",
  },
}

/**
 * Translate OTP error messages from backend to localized messages.
 * Maps exact backend messages to translation keys, falls back to original message if not recognized.
 */
function translateOtpError(message: string, config: Config): string {
  const messageToKey: Record<string, keyof typeof translation> = {
    "Too many failed attempts. Account blocked for 5 minutes.":
      "otp-too-many-attempts",
    "Invalid code. Please try again.": "otp-invalid-code",
  }

  const translationKey = messageToKey[message]
  if (translationKey) {
    return t(translation, config, translationKey)
  }

  // Fallback: return original message if not recognized
  return message
}
const defaultLogo = {
  dark: "/grezy/dark.png",
  light: "/grezy/light.png",
}

type LoginProps = {
  config: Config
  social?: boolean
  passwordReset?: boolean
  legal?: boolean
  redirectToLoggedIn?: boolean
  redirectTo?: string
  image?: {
    dark: string
    light: string
    width?: number
    height?: number
    className?: string
  }
}

export default function Login(props: LoginProps) {
  return (
    <Suspense>
      <LoginInner {...props} />
    </Suspense>
  )
}

function LoginInner({
  config,
  social = true,
  legal = true,
  redirectToLoggedIn = true,
  redirectTo = "/",
  image = defaultLogo,
}: LoginProps) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  // grezy uses a unified sign-in flow that auto-creates accounts, so there is
  // no signup/login toggle: the intent is always "login".
  const intent: AuthIntent = "login"
  // The resend button calls the same guarded endpoint as the sign-in form.
  const { getBotTrapFields } = useBotTrap()

  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(
    searchParams.get("code") !== null ? true : false,
  )
  const [isAppleLoading, setIsAppleLoading] = useState<boolean>(false)
  const [lastMethod, setLastMethod] = useState<AuthMethod | null>(null)

  useEffect(() => {
    const stored = localStorage.getItem(
      LAST_AUTH_METHOD_KEY,
    ) as AuthMethod | null
    if (stored === "email" || stored === "google" || stored === "apple") {
      setLastMethod(stored)
    }
  }, [])

  // Have the user already given his email?
  const email: string = searchParams.get("email") || ""
  const [otpValue, setOtpValue] = useState("")
  const [otpLoading, setOtpLoading] = useState(false)

  // Resend code cooldown
  const [resendState, setResendState] = useState({
    disabled: true,
    countdown: 5,
  })
  const isResendDisabled = resendState.disabled

  useEffect(() => {
    let timer: string | number | NodeJS.Timeout | undefined
    if (resendState.disabled) {
      timer = setInterval(() => {
        setResendState((prev) =>
          prev.countdown <= 1
            ? { disabled: false, countdown: 0 }
            : { ...prev, countdown: prev.countdown - 1 },
        )
      }, 1000)
    }

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [resendState.disabled])

  const deactivateResendButton = () => {
    setResendState({ disabled: true, countdown: 5 })
  }

  // Send the user into the app when the cookies already grant a session.
  // Returns true when the redirect happened.
  const goToApp = useCallback(
    (redirect: string | null): Promise<boolean> =>
      redirect
        ? redirectIfCookies(config, false, redirect)
        : redirectIfCookies(config, redirectToLoggedIn, redirectTo),
    [config, redirectToLoggedIn, redirectTo],
  )

  useEffect(() => {
    async function checkExistingSession() {
      // Coming back from the provider, the cookies were cleared before leaving,
      // so probing the session here would always fail. The provider-code effect
      // performs the redirect once the sign-in succeeds.
      if (searchParams.get("code") !== null) return
      // A staff login link signs in as its target user, not the current session.
      if (searchParams.get("otp") !== null) return
      const xRedirect = localStorage.getItem("x-redirect")
      await goToApp(xRedirect || searchParams.get("redirect"))
    }
    checkExistingSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const code = searchParams.get("code")
    if (code === null) return

    async function handleProviderCode() {
      const res = await signInWithProvider(
        "google",
        intent,
        config.APP_ID,
        code,
      )
      if (res.data === null || res.data === undefined) {
        toast.error(
          "Impossible to log in with Google. Please try again in a few minutes.",
        )
        setIsGoogleLoading(false)
        return
      }
      localStorage.setItem(LAST_AUTH_METHOD_KEY, "google")
      if (typeof res.data !== "string" && res.data?.user?.email) {
        localStorage.setItem(LAST_AUTH_EMAIL_KEY, res.data.user.email)
      }
      const xRedirect = localStorage.getItem("x-redirect")
      await goToApp(xRedirect || searchParams.get("redirect"))
    }
    handleProviderCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("code")])

  const handleOtpChange = async (code: string) => {
    const upperCode = code.toUpperCase()
    setOtpValue(upperCode)
    if (code.length === 6) {
      setOtpLoading(true)
      const res = await confirmLoginCode(email, upperCode, config.APP_ID)
      setOtpLoading(false)
      if (res.data === undefined) {
        toast.error(translateOtpError(res.message, config))
      } else {
        localStorage.setItem(LAST_AUTH_METHOD_KEY, "email")
        localStorage.setItem(LAST_AUTH_EMAIL_KEY, email)
        toast.success(t(translation, config, "connect-success"))
        const redirect = searchParams.get("redirect")
        if (redirect) {
          router.push(newFrontendURL(config, redirect, false))
        } else {
          router.push(newFrontendURL(config, redirectTo, true))
        }
      }
    }
  }

  // Login links generated by staff carry the code: submit it right away.
  useEffect(() => {
    const otp = searchParams.get("otp")
    if (email && otp) handleOtpChange(otp)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return email ? (
    <div className="mt-20">
      <HeadingText
        className="text-center"
        title={t(translation, config, "title")}
        subtitle={t(translation, config, "subtitle") + ` ${email}`}
      />
      <div className="mt-20 flex flex-col items-center space-y-6">
        <InputOTP
          className="gap-0"
          maxLength={6}
          value={otpValue}
          onChange={handleOtpChange}
        >
          {/* Mobile: single group with all slots */}
          <div className="sm:hidden">
            <InputOTPGroup className="flex">
              <InputOTPSlot className="size-13 md:size-15" index={0} />
              <InputOTPSlot className="size-13 md:size-15" index={1} />
              <InputOTPSlot className="size-13 md:size-15" index={2} />
              <InputOTPSlot className="size-13 md:size-15" index={3} />
              <InputOTPSlot className="size-13 md:size-15" index={4} />
              <InputOTPSlot className="size-13 md:size-15" index={5} />
            </InputOTPGroup>
          </div>

          {/* SM and up: keep the original first group (two slots) */}
          <div className="hidden items-center sm:flex">
            <InputOTPGroup className="">
              <InputOTPSlot className="size-13 md:size-15" index={0} />
              <InputOTPSlot className="size-13 md:size-15" index={1} />
            </InputOTPGroup>

            {/* Ensure subsequent groups/separators are hidden on small screens so we don't duplicate slots */}

            <InputOTPSeparator className="" />
            <InputOTPGroup>
              <InputOTPSlot className="size-13 md:size-15" index={2} />
              <InputOTPSlot className="size-13 md:size-15" index={3} />
            </InputOTPGroup>
            <InputOTPSeparator className="" />
            <InputOTPGroup>
              <InputOTPSlot className="size-13 md:size-15" index={4} />
              <InputOTPSlot className="size-13 md:size-15" index={5} />
            </InputOTPGroup>
          </div>
        </InputOTP>
        <Button
          variant="outline"
          size="sm"
          className="font-normal"
          disabled={isResendDisabled}
          onClick={async () => {
            // Resend reuses the address from the previous step: nothing to
            // type here, so the fill-time floor does not apply.
            const res = await requestLoginCode(
              email,
              config.LOCALE,
              intent,
              config.APP_ID,
              getBotTrapFields(true),
            )
            if (res.data === undefined) {
              toast.error(res.message)
            } else {
              toast.info(t(translation, config, "resend-toast"))
              deactivateResendButton()
            }
          }}
        >
          {t(translation, config, "resend")}
        </Button>
        {otpLoading && <Spinner />}
      </div>
    </div>
  ) : (
    <div className="flex justify-center pt-20">
      <div className="flex w-3/4 flex-col space-y-4 text-center lg:w-lg">
        <div className="-mt-20 hidden md:block">
          <div className="flex flex-col items-center space-y-8">
            <div className="relative">
              <div className="flex items-center justify-center">
                <Image
                  src={image.light}
                  alt="Logo"
                  width={image.width ?? 64}
                  height={image.height ?? 64}
                  priority
                  className={cn(
                    "block dark:hidden",
                    image.className ?? "size-20 rounded-lg",
                  )}
                />
                <Image
                  src={image.dark}
                  alt="Logo"
                  width={image.width ?? 64}
                  height={image.height ?? 64}
                  priority
                  className={cn(
                    "hidden dark:block",
                    image.className ?? "size-20 rounded-lg",
                  )}
                />
              </div>
            </div>
            <HeadingText
              title={t(translation, config, "login")}
              subtitle={t(translation, config, "welcome")}
            />
          </div>
          <p className="text-muted-foreground mt-3 text-center text-sm">
            {t(translation, config, "no-account")}
          </p>
        </div>
        <div className="pt-10 pb-4 md:hidden">
          <HeadingText
            title={t(translation, config, "login")}
            subtitle={t(translation, config, "welcome")}
          />
          <p className="text-muted-foreground mt-1 text-sm">
            {t(translation, config, "no-account")}
          </p>
        </div>

        <div className="z-0 px-2">
          <div className="relative">
            {lastMethod === "email" && (
              <LastUsedPill label={t(translation, config, "last-used")} />
            )}
            <SignInForm
              config={config}
              mode={intent}
              redirectTo={
                pathname + "?redirect=" + (searchParams.get("redirect") || "")
              }
            />
          </div>
          {social && (
            <>
              <div className="flex items-center gap-4 py-2">
                <Separator className="flex-1" />
                <span className="text-muted-foreground text-sm uppercase">
                  {t(translation, config, "or")}
                </span>
                <Separator className="flex-1" />
              </div>
              <div className="flex flex-row gap-4 py-1">
                <div className="relative w-full shrink-1">
                  {lastMethod === "google" && (
                    <LastUsedPill label={t(translation, config, "last-used")} />
                  )}
                  <Button
                    disabled={isGoogleLoading}
                    className="w-full"
                    variant={"outline"}
                    onClick={async () => {
                      setIsGoogleLoading(true)
                      localStorage.removeItem("x-redirect")
                      const redirect = searchParams.get("redirect")
                      if (redirect) {
                        localStorage.setItem("x-redirect", redirect)
                      }
                      // Cookies already in the jar may still grant a session
                      // (possibly after a token refresh): go straight in.
                      // Otherwise they are stale, so wipe them before asking
                      // Google for a fresh identity.
                      if (await goToApp(redirect)) return
                      await clearAuthCookies()
                      const res = await signInWithProvider(
                        "google",
                        intent,
                        config.APP_ID,
                      )
                      if (typeof res.data === "string") {
                        // Cross-origin redirect (Google OAuth): router.push is
                        // for in-app navigation and no-ops unreliably on
                        // external URLs, leaving the button stuck disabled.
                        window.location.href = res.data
                      } else {
                        toast.error(
                          "Impossible to log in with Google. Please try again in a few minutes.",
                        )
                        setIsGoogleLoading(false)
                      }
                    }}
                  >
                    <FcGoogle className="mr-2 size-5" />
                    {isGoogleLoading ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <span className="hidden sm:block">{"Google"}</span>
                    )}
                  </Button>
                </div>
                <div className="relative w-full shrink-1">
                  {lastMethod === "apple" && (
                    <LastUsedPill label={t(translation, config, "last-used")} />
                  )}
                  <Button
                    // Remove the two following lines when Apple Provider is ready for login.
                    // And uncomment the third line
                    className="pointer-events-auto w-full cursor-not-allowed opacity-50"
                    aria-disabled="true"
                    // disabled={isAppleLoading}
                    disabled={true}
                    variant={"outline"}
                    tabIndex={-1}
                    onClick={async () => {
                      setIsAppleLoading(true)
                      const res = await signInWithProvider(
                        "apple",
                        intent,
                        config.APP_ID,
                      )
                      if (typeof res.data === "string") {
                        router.push(res.data)
                      } else {
                        toast.error(
                          "Impossible to log in with Apple. Please try again in a few minutes.",
                        )
                        setIsAppleLoading(false)
                      }
                    }}
                  >
                    <FaApple className="mr-2 size-5" />
                    {isAppleLoading ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <span className="hidden sm:block">{"Apple"}</span>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
        {legal && (
          <div>
            <div className="text-muted-foreground text-xs">
              {t(translation, config, "legal-1")}

              <Link
                className="text-muted-foreground underline underline-offset-4"
                href="/terms"
              >
                {t(translation, config, "legal-2")}
              </Link>
              {", "}
              <Link
                className="text-muted-foreground underline underline-offset-4"
                href="/privacy"
              >
                {t(translation, config, "legal-3")}
              </Link>
              {"  "}
              {t(translation, config, "and")}
              {"  "}
              <Link
                className="text-muted-foreground underline underline-offset-4"
                href="/legal"
              >
                {t(translation, config, "legal-4")}
              </Link>
              {"."}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function LastUsedPill({ label }: { label: string }) {
  return (
    <Badge
      variant="secondary"
      className="absolute top-1 right-3 z-10 h-4 rounded-sm px-1.5 py-0 text-[9px] leading-none font-medium tracking-wide uppercase shadow-sm"
    >
      {label}
    </Badge>
  )
}
